import { memo, useRef } from "react"
import { CustomColumnDef } from "./CustomTable"
import { Box, TableCell } from "@mui/material"
import SubtleShimmer from "../common/SubtleShimmer"

interface TableRowCellProps<T> {
	column: CustomColumnDef<T>
	row: T
	rowKey: string | number
	index: number
	columnIndex: number
	columnLength: number
	verticalDividers: boolean
	skeletalLoading: boolean
	/** When true, cell content is mounted once; live fields update themselves. */
	stableLiveContent?: boolean
}

const TableRowCell = <T,>({
		column,
		row,
		rowKey,
		columnLength,
		index,
		columnIndex,
		verticalDividers,
		skeletalLoading = false,
		stableLiveContent = false,
	}: TableRowCellProps<T>) => {
	const cellIdentityKey = column.cellIdentityKey ?? `${column.field}:${columnIndex}`
	const frozenSlotKey = `${cellIdentityKey}:${columnIndex}`
	const frozenIdentityRef = useRef<string | null>(null)
	const frozenContentRef = useRef<React.ReactNode>(null)
	if (stableLiveContent && frozenIdentityRef.current !== frozenSlotKey) {
		frozenIdentityRef.current = frozenSlotKey
		frozenContentRef.current = null
	}
	if (stableLiveContent && frozenContentRef.current === null && column.cellRenderer) {
		const rendered = column.cellRenderer(row, index)
		const resolved = rendered instanceof Promise ? null : rendered
		frozenContentRef.current = (resolved as any)?.props?.text === "NaN" ? "-" : resolved
	}

	const content = stableLiveContent
		? frozenContentRef.current
		: column.cellRenderer
			? column.cellRenderer(row, index)
			: (row as any)[(column.field as string)]

	return (
		<TableCell
			className={column.tableCellClassName ? column.tableCellClassName : ''}
			align={column.align || 'left'}
			sx={{
				position: 'relative',
				borderRight: columnIndex < columnLength - 1 && verticalDividers ? (theme) => `1px solid ${theme.palette.divider}` : 'none !important',
				borderBottomColor: (theme) => theme.palette.divider,
				...column.sx
			}}>
			{skeletalLoading ? (
				<SubtleShimmer loading>
					<Box sx={{ height: 34, width: '100%' }} />
				</SubtleShimmer>
			) : (
				<>{content}</>
			)}
		</TableCell>
	)
}

const getRowCellValue = <T,>(props: TableRowCellProps<T>) => {
	const field = props.column.field
	if (!field) return undefined
	return (props.row as Record<string, unknown>)[field]
}

const isLayoutEqual = <T,>(prev: TableRowCellProps<T>, next: TableRowCellProps<T>) =>
	prev.rowKey === next.rowKey &&
	prev.columnIndex === next.columnIndex &&
	prev.column.field === next.column.field &&
	prev.column.cellIdentityKey === next.column.cellIdentityKey &&
	prev.column.colSpan === next.column.colSpan &&
	prev.skeletalLoading === next.skeletalLoading &&
	prev.stableLiveContent === next.stableLiveContent &&
	prev.verticalDividers === next.verticalDividers &&
	prev.columnLength === next.columnLength

/** Option chain (stableLiveContent): freeze DOM. Screener: re-render when row data changes. */
const areTableRowCellPropsEqual = <T,>(prev: TableRowCellProps<T>, next: TableRowCellProps<T>) => {
	if (prev.stableLiveContent && next.stableLiveContent) {
		return isLayoutEqual(prev, next)
	}

	if (prev.row !== next.row) return false
	if (prev.column !== next.column) return false
	if (getRowCellValue(prev) !== getRowCellValue(next)) return false

	return isLayoutEqual(prev, next)
}

export default memo(TableRowCell, areTableRowCellPropsEqual) as <T>(props: TableRowCellProps<T>) => JSX.Element
